import React from "react";
import {
  Button,
  Box,
  Checkbox,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowBack, FilterList } from "@mui/icons-material";

import BaselineEmission from "pages/PortfolioView/BaselineEmission/BaselineEmission";
import { _getPortfolioGlidepath, } from "middlewares/DashboardApi/portfolioView";
import { _getScenarioSectionsByLocationIds } from "middlewares/DashboardApi/scenarioView";
import { LocationScenarioSectionsModel } from "pages/Dashboards/ScenarioView/data/LocationScenarioSectionsModel";
import { PortfolioGlidepathFilter, PortfolioGlidepathResult } from "./Data/PortfolioGlidepathModels";
import { PortfolioFilter } from "./Data/PortfolioFilter";
import ProjectRegisterSection from "./ProjectRegisterSection";
import DecarbPlansDrawer, { DecarbPlansApplyMetadata } from "./DecarbPlansDrawer";
import NotificationHelper from "services/notification-helper";

// Scopes constant array
const SCOPE_OPTIONS = [
  { id: 1, label: "Scope 1" },
  { id: 2, label: "Scope 2" },
  { id: 3, label: "Scope 3" },
];

const ACCENT = "#E57A1F";
const CHECKBOX_SX = { color: ACCENT, "&.Mui-checked": { color: ACCENT } };
const SELECT_SX = {
  borderRadius: "6px",
  backgroundColor: "#fff",
  fontSize: "0.85rem",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#d1d5db",
  },
};
const SELECT_MENU_PROPS = {
  MenuListProps: { dense: true },
  PaperProps: {
    sx: { "& .MuiMenuItem-root": { fontSize: "0.85rem", minHeight: 32, py: 0.25, px: 1 } },
  },
};

type ReturnState = {
  returnTo?: string;
  returnNavState?: {
    clientId?: number;
    orgId?: number;
    viewLevel?: "client" | "organization" | "location";
  };
};

interface Organization {
  orgId: number;
  orgName: string;
  locationIds?: number[];
}

interface SelectedOrganization {
  orgId: number;
  orgName: string;
}

interface LocationItem {
  locationId: number;
  locationName: string;
}

interface PortfolioViewProps {
  onBack?: () => void;
  clientId?: number;
  orgId?: number;
  IsClientView?: boolean;
  portfolioTitle?: string;
  clientName?: string;
  organizationName?: string;
  locationCount?: number;
  totalEmissions?: number;
  scope1Total?: number;
  scope2Total?: number;
  scope3Total?: number;
  userRole?: string;
  organizationsList?: Organization[];
  locationsList?: LocationItem[];
}

const resolveLocationIds = (
  isClientView: boolean,
  organizations: Organization[],
  locations: LocationItem[],
  selectedOrgIds: number[],
  selectedLocIds: number[]
): number[] => {
  if (isClientView) {
    const orgIds = selectedOrgIds.length > 0 ? selectedOrgIds : organizations.map((o) => o.orgId);
    const ids = organizations
      .filter((o) => orgIds.includes(o.orgId))
      .flatMap((o) => o.locationIds ?? []);
    return [...new Set(ids)].filter(Number.isInteger);
  }
  const ids = selectedLocIds.length > 0 ? selectedLocIds : locations.map((l) => l.locationId);
  return [...new Set(ids)].filter(Number.isInteger);
};

const applySelectAll = <T,>(
  onChange: (values: T[]) => void,
  allValues: T[],
  checked: boolean
): void => {
  onChange(checked ? allValues : []);
};

const toScopeIds = (scopeLabels: string[]): number[] =>
  scopeLabels.map((label) => SCOPE_OPTIONS.find((s) => s.label === label)?.id ?? 0);

/* Client view drives the selection off organizations, organization view off locations. */
type FilterSelection = {
  organizations: number[];
  locations: number[];
};

const EMPTY_SELECTION: FilterSelection = { organizations: [], locations: [] };

const getDefaultFilterSelection = (
  isClientView: boolean,
  organizations: Organization[],
  locations: LocationItem[]
): FilterSelection | null => {
  const hasRequiredItems = isClientView ? organizations.length > 0 : locations.length > 0;
  if (!hasRequiredItems) {
    return null;
  }

  return isClientView
    ? { organizations: organizations.map((org) => org.orgId), locations: [] }
    : { organizations: [], locations: locations.map((loc) => loc.locationId) };
};

const getInitialPortfolioFilter = (
  isClientView: boolean,
  organizations: Organization[],
  locations: LocationItem[]
): PortfolioFilter => ({
  LocationId: isClientView
    ? [...new Set(organizations.flatMap((org) => org.locationIds ?? []))].filter(Number.isInteger)
    : [...new Set(locations.map((loc) => loc.locationId))].filter(Number.isInteger),
  BaseYear: [],
});

/* A location is customised only when its applied section differs from its default one. */
const countCustomDecarbPlans = (
  sections: LocationScenarioSectionsModel[],
  appliedSections: Record<number, string>
): number =>
  sections.filter((ls) => {
    const applied = appliedSections[ls.locationId];
    const defaultSection = ls.scenarioSections[0] ?? "";
    return applied !== undefined && applied !== defaultSection;
  }).length;

const getDefaultScenarioSection = (sections: string[]): string => sections[0] ?? "";

type ReconciledScenarioSelection = {
  selections: Record<number, string>;
  replacedSelections: number[];
  unavailablePreferredSelections: number[];
};

const getUnavailablePlanMessage = (planName?: string): string => {
  const planLabel = planName ? `"${planName}"` : "The selected decarbonization plan";
  return `${planLabel} is unavailable for one or more selected locations. Those locations are using their first available plan.`;
};

export const reconcileScenarioSelections = (
  selectedLocationIds: number[],
  locationSections: LocationScenarioSectionsModel[],
  currentSelections: Record<number, string>,
  preferredSection?: string | null
): ReconciledScenarioSelection => {
  const sectionsByLocationId = new Map(
    locationSections.map((section) => [section.locationId, section.scenarioSections ?? []])
  );
  const selections: Record<number, string> = {};
  const replacedSelections: number[] = [];
  const unavailablePreferredSelections: number[] = [];

  selectedLocationIds.forEach((locationId) => {
    const hasKnownAvailability = sectionsByLocationId.has(locationId);
    const availableSections = sectionsByLocationId.get(locationId) ?? [];
    const currentSelection = currentSelections[locationId];

    if (!hasKnownAvailability) {
      if (currentSelection) {
        selections[locationId] = currentSelection;
      }
      return;
    }

    if (currentSelection && availableSections.includes(currentSelection)) {
      selections[locationId] = currentSelection;
      return;
    }

    if (preferredSection && availableSections.includes(preferredSection)) {
      selections[locationId] = preferredSection;
      return;
    }

    const defaultSection = getDefaultScenarioSection(availableSections);
    if (defaultSection) {
      selections[locationId] = defaultSection;
    }

    if (currentSelection) {
      replacedSelections.push(locationId);
    } else if (preferredSection && hasKnownAvailability) {
      unavailablePreferredSelections.push(locationId);
    }
  });

  return { selections, replacedSelections, unavailablePreferredSelections };
};

/* Organization view shows a single organization; its name falls back through the props. */
const getOrgForNonClientView = (
  orgId: number | undefined,
  organizationName: string | undefined,
  organizationsList: Organization[]
): SelectedOrganization[] => {
  if (orgId == null) {
    return [];
  }

  const nonEmptyOrganizationName =
    organizationName != null && organizationName !== "" ? organizationName : undefined;

  return [
    {
      orgId,
      orgName:
        nonEmptyOrganizationName ??
        organizationsList.find((organization) => organization.orgId === orgId)?.orgName ??
        `Organization ${orgId}`,
    },
  ];
};

/* Baseline emissions are reported per organization: every selected one in client view,
   the single organization in scope otherwise. */
const getBaselineOrganizations = (
  isClientView: boolean,
  orgId: number | undefined,
  organizationName: string | undefined,
  organizationsList: Organization[],
  selectedOrganizations: number[]
): SelectedOrganization[] => {
  if (!isClientView) {
    return getOrgForNonClientView(orgId, organizationName, organizationsList);
  }

  if (selectedOrganizations.length === 0) {
    return organizationsList;
  }

  return organizationsList.filter((org) => selectedOrganizations.includes(org.orgId));
};

type GlidepathInputs = {
  clientId?: number;
  orgId?: number;
  isClientView: boolean;
  selectedOrganizations: number[];
  selectedScopeFilter: string[];
  appliedScenarioSections: Record<number, string>;
  getLocationIds: () => number[];
};

const resolveOrganizationIds = (
  isClientView: boolean,
  orgId: number | undefined,
  selectedOrganizations: number[]
): number[] | undefined => {
  if (isClientView) {
    return selectedOrganizations;
  }
  return orgId ? [orgId] : undefined;
};

const buildGlidepathFilter = (
  inputs: GlidepathInputs,
  locationIds: number[]
): PortfolioGlidepathFilter => ({
  level: inputs.isClientView || !inputs.orgId ? "Client" : "Organization",
  clientId: inputs.clientId ?? 0,
  organizationIds: resolveOrganizationIds(
    inputs.isClientView,
    inputs.orgId,
    inputs.selectedOrganizations
  ),
  locationParams: locationIds.map((id) => ({
    locationId: id,
    scenarioSection: inputs.appliedScenarioSections[id] ?? undefined,
  })),
  scopes: inputs.selectedScopeFilter.length > 0 ? toScopeIds(inputs.selectedScopeFilter) : [1, 2, 3],
});

const useBackHandler = (onBack?: () => void): (() => void) => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as ReturnState;

  return () => {
    if (onBack) {
      onBack();
      return;
    }

    if (state.returnTo) {
      navigate(state.returnTo, { state: state.returnNavState });
      return;
    }

    navigate(-1);
  };
};

/* Fetch available scenario sections once we know which location IDs are in scope */
const useScenarioSections = (
  isClientView: boolean,
  organizationsList: Organization[],
  locationsList: LocationItem[]
): LocationScenarioSectionsModel[] => {
  const [locationSections, setLocationSections] = React.useState<LocationScenarioSectionsModel[]>(
    []
  );

  const scenarioLocationIds = React.useMemo(
    () =>
      resolveLocationIds(
        isClientView,
        organizationsList,
        locationsList,
        isClientView ? organizationsList.map((o) => o.orgId) : [],
        isClientView ? [] : locationsList.map((l) => l.locationId)
      ),
    [isClientView, organizationsList, locationsList]
  );

  const scenarioLocationIdsKey = React.useMemo(
    () => scenarioLocationIds.join(","),
    [scenarioLocationIds]
  );

  React.useEffect(() => {
    const locationIds = scenarioLocationIds;
    if (locationIds.length === 0) return;

    _getScenarioSectionsByLocationIds(locationIds)
      .then((data) => {
        if (data && Array.isArray(data)) {
          setLocationSections(data);
        }
      })
      .catch((err) => console.error("Failed to fetch scenario sections:", err));
  }, [scenarioLocationIdsKey]);

  return locationSections;
};

/* Apply the default selection once — as soon as the lists that drive it have arrived */
const useDefaultFilterSelection = (
  isClientView: boolean,
  organizationsList: Organization[],
  locationsList: LocationItem[],
  apply: (selection: FilterSelection) => void
): void => {
  const hasInitialized = React.useRef(false);
  const applyRef = React.useRef(apply);
  applyRef.current = apply;

  React.useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    const selection = getDefaultFilterSelection(isClientView, organizationsList, locationsList);
    if (!selection) {
      return;
    }

    hasInitialized.current = true;
    applyRef.current(selection);
  }, [isClientView, locationsList, organizationsList]);
};

const usePortfolioGlidepath = (
  inputs: GlidepathInputs,
  isActive: boolean,
  searchClickVersion: number
): PortfolioGlidepathResult | null => {
  const [portfolioGlidepathData, setPortfolioGlidepathData] =
    React.useState<PortfolioGlidepathResult | null>(null);

  // Stable ref that always holds the latest filter values.
  // The fetch callback reads from here so it doesn't need to list every filter
  // as a dependency — keeping it stable and avoiding spurious re-fetches
  // whenever a filter dropdown changes before "Apply" is clicked.
  const inputsRef = React.useRef(inputs);
  inputsRef.current = inputs;

  const fetchPortfolioGlidepath = React.useCallback(async () => {
    try {
      const current = inputsRef.current;
      const locationIds = current.getLocationIds();
      if (locationIds.length === 0) return;

      const result = await _getPortfolioGlidepath(buildGlidepathFilter(current, locationIds));
      setPortfolioGlidepathData(result);
    } catch (err) {
      console.error("Error fetching portfolio glidepath:", err);
      setPortfolioGlidepathData(null);
    }
  }, []);

  React.useEffect(() => {
    if (isActive) {
      fetchPortfolioGlidepath();
    }
  }, [isActive, searchClickVersion, fetchPortfolioGlidepath]);

  return portfolioGlidepathData;
};

type SelectValue = number | string;

interface MultiSelectOption {
  value: SelectValue;
  label: string;
}

interface MultiSelectFilterProps {
  options: MultiSelectOption[];
  selected: SelectValue[];
  onChange: (values: SelectValue[]) => void;
  allSelectedLabel: string;
  minWidth: number;
}

/* Multi-select dropdown with a "Select All" row; shared by the entity and scope filters. */
const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
  options,
  selected,
  onChange,
  allSelectedLabel,
  minWidth,
}) => (
  <FormControl variant="outlined" size="small" sx={{ minWidth }}>
    <Select
      multiple
      displayEmpty
      value={selected}
      onChange={(e) => {
        const value = e.target.value as SelectValue[];
        onChange(typeof value === "string" ? [] : value);
      }}
      MenuProps={SELECT_MENU_PROPS}
      renderValue={(value) =>
        value.length === 0 || value.length === options.length
          ? allSelectedLabel
          : `${value.length} selected`
      }
      sx={SELECT_SX}
    >
      <MenuItem>
        <Checkbox
          checked={selected.length === options.length}
          indeterminate={selected.length > 0 && selected.length < options.length}
          onChange={(e) =>
            applySelectAll(
              onChange,
              options.map((option) => option.value),
              e.target.checked
            )
          }
          size="small"
          sx={CHECKBOX_SX}
        />
        <Typography sx={{ fontSize: "0.85rem" }}>Select All</Typography>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <Checkbox checked={selected.includes(option.value)} size="small" sx={CHECKBOX_SX} />
          <Typography sx={{ fontSize: "0.85rem" }}>{option.label}</Typography>
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

interface PortfolioHeaderProps {
  portfolioTitle: string;
  isClientView: boolean;
  locationCount?: number;
  organizationCount: number;
  onBack: () => void;
}

const PortfolioHeader: React.FC<PortfolioHeaderProps> = ({
  portfolioTitle,
  isClientView,
  locationCount,
  organizationCount,
  onBack,
}) => {
  const locations = locationCount ?? 1;
  const locationSuffix = locations !== 1 ? "s" : "";
  const organizationSuffix = organizationCount !== 1 ? "s" : "";

  return (
    <>
      {/* Header with Back Button and Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: "#64748b", p: 0.5 }}>
          <ArrowBack sx={{ fontSize: 20 }} />
        </IconButton>
        <Typography sx={{ fontSize: "1.10rem", fontWeight: 700, color: "#0f2b52" }}>
          {portfolioTitle}
        </Typography>
      </Box>

      {/* Subtitle */}
      <Typography sx={{ color: "#64748b", fontSize: "0.85rem", mb: 2, pl: 5 }}>
        {isClientView
          ? `Aggregated across ${locations} location${locationSuffix} of ${organizationCount} organization${organizationSuffix}`
          : `Aggregated across ${locations} location${locationSuffix}`}
      </Typography>
    </>
  );
};

type TabKey = "baseline" | "project";

interface PortfolioTabsProps {
  activeTab: TabKey;
  onTabChange: (tab: TabKey) => void;
}

const TAB_ITEMS: { key: TabKey; label: string }[] = [
  { key: "baseline", label: "Baseline Emissions" },
  { key: "project", label: "Project Register" },
];

const PortfolioTabs: React.FC<PortfolioTabsProps> = ({ activeTab, onTabChange }) => (
  <Box sx={{ display: "flex", gap: 3, borderBottom: "1px solid #e5e7eb", mb: 2.5, pl: 0 }}>
    {TAB_ITEMS.map((tab) => {
      const isActive = activeTab === tab.key;
      return (
        <Typography
          key={tab.key}
          onClick={() => onTabChange(tab.key)}
          sx={{
            fontSize: "0.95rem",
            fontWeight: isActive ? 500 : 400,
            color: isActive ? ACCENT : "#64748b",
            borderBottom: isActive ? `2px solid ${ACCENT}` : "2px solid transparent",
            pb: 1.2,
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { color: "#475569" },
          }}
        >
          {tab.label}
        </Typography>
      );
    })}
  </Box>
);

interface PortfolioFiltersProps {
  isClientView: boolean;
  isProjectTab: boolean;
  entityOptions: MultiSelectOption[];
  entitySelected: number[];
  entityAllSelectedLabel: string;
  onEntityChange: (values: number[]) => void;
  scopeSelected: string[];
  onScopeChange: (values: string[]) => void;
  groupBy: string;
  onGroupByChange: (value: string) => void;
  customDecarbCount: number;
  onOpenDecarbPlans: () => void;
  onApply: () => void;
}

const PortfolioFilters: React.FC<PortfolioFiltersProps> = ({
  isClientView,
  isProjectTab,
  entityOptions,
  entitySelected,
  entityAllSelectedLabel,
  onEntityChange,
  scopeSelected,
  onScopeChange,
  groupBy,
  onGroupByChange,
  customDecarbCount,
  onOpenDecarbPlans,
  onApply,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      mb: 2.5,
    }}
  >
    <FilterList sx={{ color: ACCENT, fontSize: 20 }} />
    <Typography sx={{ color: "#475569", fontWeight: 500, fontSize: "0.9rem" }}>Filters:</Typography>

    {/* Dynamic Dropdown: Organizations (Client View) or Locations (Organization View) */}
    <MultiSelectFilter
      options={entityOptions}
      selected={entitySelected}
      onChange={(values) => onEntityChange(values as number[])}
      allSelectedLabel={entityAllSelectedLabel}
      minWidth={220}
    />

    <MultiSelectFilter
      options={SCOPE_OPTIONS.map((scope) => ({ value: scope.label, label: scope.label }))}
      selected={scopeSelected}
      onChange={(values) => onScopeChange(values as string[])}
      allSelectedLabel="All Scopes"
      minWidth={160}
    />

    {/* Group By dropdown — only visible on the Project Register tab */}
    {isProjectTab && (
      <FormControl variant="outlined" size="small" sx={{ minWidth: 190 }}>
        <Select value={groupBy} onChange={(e) => onGroupByChange(e.target.value)} sx={SELECT_SX}>
          <MenuItem value={isClientView ? "organization" : "location"} sx={{ fontSize: "0.85rem" }}>
            Group by {isClientView ? "Organization" : "Location"}
          </MenuItem>
          <MenuItem value="measureType" sx={{ fontSize: "0.85rem" }}>Group by Measure Type</MenuItem>
          <MenuItem value="measureCategory" sx={{ fontSize: "0.85rem" }}>Group by Measure Category</MenuItem>
        </Select>
      </FormControl>
    )}

    {/* Decarb Plans button — only visible on the Project Register tab */}
    {isProjectTab && (
      <Button
        variant="outlined"
        size="small"
        onClick={onOpenDecarbPlans}
        sx={{
          height: 35,
          borderRadius: "6px",
          px: 1.5,
          textTransform: "none",
          fontSize: "0.80rem",
          fontWeight: 590,
          borderColor: ACCENT,
          color: ACCENT,
          whiteSpace: "nowrap",
          "&:hover": { borderColor: "#cf6915", color: "#cf6915", backgroundColor: "rgba(229,122,31,0.06)" },
        }}
      >
        ☰ Decarb Plans{customDecarbCount > 0 ? ` (${customDecarbCount})` : ""}
      </Button>
    )}
    <Button
      variant="contained"
      onClick={onApply}
      sx={{
        height: 35,
        ml: "auto",
        borderRadius: "6px",
        px: 2,
        textTransform: "none",
        fontSize: "0.80rem",
        fontWeight: 590,
        backgroundColor: ACCENT,
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "#cf6915",
          boxShadow: "none",
        },
      }}
    >
      Apply
    </Button>
  </Box>
);

const toEntityOptions = (
  isClientView: boolean,
  organizationsList: Organization[],
  locationsList: LocationItem[]
): MultiSelectOption[] =>
  isClientView
    ? organizationsList.map((org) => ({ value: org.orgId, label: org.orgName }))
    : locationsList.map((loc) => ({ value: loc.locationId, label: loc.locationName }));

const PortfolioView: React.FC<PortfolioViewProps> = ({
  onBack,
  clientId,
  orgId,
  IsClientView,
  portfolioTitle = "Organization Portfolio",
  locationCount,
  userRole,
  organizationName,
  organizationsList = [],
  locationsList = [],
}) => {
  const isClientView = !!IsClientView;
  const handleBack = useBackHandler(onBack);

  // This is dummy change for triggering the build pod

  const initialSelection = getDefaultFilterSelection(isClientView, organizationsList, locationsList) ?? EMPTY_SELECTION;

  const [selectedOrganizations, setSelectedOrganizations] = React.useState<number[]>(
    initialSelection.organizations
  );
  const [selectedLocationsFilter, setSelectedLocationsFilter] = React.useState<number[]>(
    initialSelection.locations
  );
  /* Header counts reflect the last applied selection, not the pending dropdown state. */
  const [appliedSelection, setAppliedSelection] = React.useState<FilterSelection>(initialSelection);


  const [selectedScopeFilter, setSelectedScopeFilter] = React.useState<string[]>(
    SCOPE_OPTIONS.map((scope) => scope.label)
  );
  const [projectChartGroupBy, setProjectChartGroupBy] = React.useState<string>(
    isClientView ? "organization" : "location"
  );
  const [activeTab, setActiveTab] = React.useState<TabKey>("baseline");
  const [projectSearchVersion, setProjectSearchVersion] = React.useState(0);
  const [searchClickVersion, setSearchClickVersion] = React.useState(0);
  const [portfolioFilter, setPortfolioFilter] = React.useState<PortfolioFilter>(() =>
    getInitialPortfolioFilter(isClientView, organizationsList, locationsList)
  );

  const [decarbPlansOpen, setDecarbPlansOpen] = React.useState(false);
  const [appliedScenarioSections, setAppliedScenarioSections] = React.useState<
    Record<number, string>
  >({});
  const [preferredScenarioSection, setPreferredScenarioSection] = React.useState<string | null>(null);
  const [decarbPlanGuidance, setDecarbPlanGuidance] = React.useState<string | null>(null);
  const appliedScenarioSectionsRef = React.useRef(appliedScenarioSections);
  appliedScenarioSectionsRef.current = appliedScenarioSections;

  const isProjectTab = activeTab === "project";
  const locationSections = useScenarioSections(isClientView, organizationsList, locationsList);

  const getLocationIdsForSelection = React.useCallback(
    (): number[] =>
      resolveLocationIds(
        isClientView,
        organizationsList,
        locationsList,
        selectedOrganizations,
        selectedLocationsFilter
      ),
    [isClientView, locationsList, organizationsList, selectedLocationsFilter, selectedOrganizations]
  );

  useDefaultFilterSelection(isClientView, organizationsList, locationsList, (selection) => {
    setSelectedOrganizations(selection.organizations);
    setSelectedLocationsFilter(selection.locations);
    setAppliedSelection(selection);
    setSelectedScopeFilter(SCOPE_OPTIONS.map((scope) => scope.label));
    setSearchClickVersion((prev) => prev + 1);
  });

  const selectedLocationIds = React.useMemo(
    () => getLocationIdsForSelection(),
    [getLocationIdsForSelection]
  );

  const filteredLocationSections = React.useMemo(
    () => locationSections.filter((ls) => selectedLocationIds.includes(ls.locationId)),
    [locationSections, selectedLocationIds]
  );

  const normalizedScenarioSections = React.useMemo(
    () =>
      reconcileScenarioSelections(
        selectedLocationIds,
        filteredLocationSections,
        appliedScenarioSections,
        preferredScenarioSection
      ).selections,
    [appliedScenarioSections, filteredLocationSections, preferredScenarioSection, selectedLocationIds]
  );

  React.useEffect(() => {
    const { selections, replacedSelections, unavailablePreferredSelections } = reconcileScenarioSelections(
      selectedLocationIds,
      filteredLocationSections,
      appliedScenarioSectionsRef.current,
      preferredScenarioSection
    );

    setAppliedScenarioSections((currentSelections) =>
      JSON.stringify(selections) === JSON.stringify(currentSelections) ? currentSelections : selections
    );

    if (replacedSelections.length > 0 || unavailablePreferredSelections.length > 0) {
      const message = getUnavailablePlanMessage(preferredScenarioSection ?? undefined);
      setDecarbPlanGuidance(message);
      NotificationHelper.notifyWarning(message);
    }
  }, [filteredLocationSections, preferredScenarioSection, selectedLocationIds]);

  const portfolioGlidepathData = usePortfolioGlidepath(
    {
      clientId,
      orgId,
      isClientView,
      selectedOrganizations,
      selectedScopeFilter,
      appliedScenarioSections: normalizedScenarioSections,
      getLocationIds: getLocationIdsForSelection,
    },
    isProjectTab,
    searchClickVersion
  );

  const handleSearchClick = () => {
    setPortfolioFilter({
      LocationId: getLocationIdsForSelection(),
      BaseYear: [],
    });
    setAppliedSelection({
      organizations: selectedOrganizations,
      locations: selectedLocationsFilter,
    });
    setSearchClickVersion((prev) => prev + 1);

    if (isProjectTab) {
      setProjectSearchVersion((prev) => prev + 1);
    }
  };

  const handleApplyDecarbPlans = (
    selections: Record<number, string>,
    metadata?: DecarbPlansApplyMetadata
  ) => {
    const nextPreferredSection = metadata?.requestedSection ?? preferredScenarioSection;
    if (metadata?.requestedSection) {
      setPreferredScenarioSection(metadata.requestedSection);
    }

    const reconciled = reconcileScenarioSelections(
      selectedLocationIds,
      filteredLocationSections,
      selections,
      nextPreferredSection
    );

    setAppliedScenarioSections(reconciled.selections);
    setDecarbPlansOpen(false);
    if (
      reconciled.replacedSelections.length > 0 ||
      reconciled.unavailablePreferredSelections.length > 0 ||
      (metadata?.unavailableLocationIds?.length ?? 0) > 0
    ) {
      const message = getUnavailablePlanMessage(nextPreferredSection ?? undefined);
      setDecarbPlanGuidance(message);
      NotificationHelper.notifyWarning(message);
    } else {
      setDecarbPlanGuidance(null);
    }
    if (isProjectTab) {
      setSearchClickVersion((prev) => prev + 1);
    }
  };

  const appliedLocationCount = resolveLocationIds(
    isClientView,
    organizationsList,
    locationsList,
    appliedSelection.organizations,
    appliedSelection.locations
  ).length;

  /* The prop is only a fallback for the initial render, before the lists that drive the count arrive. */
  const canComputeLocationCount = isClientView
    ? organizationsList.length > 0
    : locationsList.length > 0;

  return (
    <Box
      sx={{
        zoom: 0.75,
      }}
      data-client-id={clientId}
      data-org-id={orgId}
      data-is-client-view={isClientView ? "true" : "false"}
    >
      <PortfolioHeader
        portfolioTitle={portfolioTitle}
        isClientView={isClientView}
        locationCount={canComputeLocationCount ? appliedLocationCount : locationCount}
        organizationCount={
          appliedSelection.organizations.length > 0
            ? appliedSelection.organizations.length
            : organizationsList.length
        }
        onBack={handleBack}
      />

      <PortfolioTabs activeTab={activeTab} onTabChange={setActiveTab} />

      <PortfolioFilters
        isClientView={isClientView}
        isProjectTab={isProjectTab}
        entityOptions={toEntityOptions(isClientView, organizationsList, locationsList)}
        entitySelected={isClientView ? selectedOrganizations : selectedLocationsFilter}
        entityAllSelectedLabel={isClientView ? "All Organizations" : "All Locations"}
        onEntityChange={isClientView ? setSelectedOrganizations : setSelectedLocationsFilter}
        scopeSelected={selectedScopeFilter}
        onScopeChange={setSelectedScopeFilter}
        groupBy={projectChartGroupBy}
        onGroupByChange={setProjectChartGroupBy}
        customDecarbCount={countCustomDecarbPlans(filteredLocationSections, normalizedScenarioSections)}
        onOpenDecarbPlans={() => setDecarbPlansOpen(true)}
        onApply={handleSearchClick}
      />
      {isProjectTab && decarbPlanGuidance && (
        <Box
          sx={{
            mb: 2,
            px: 1.5,
            py: 1,
            border: "1px solid #fbbf24",
            borderRadius: "8px",
            backgroundColor: "#fffbeb",
            color: "#92400e",
            fontSize: "0.8rem",
            lineHeight: 1.4,
          }}
          role="status"
        >
          {decarbPlanGuidance}
        </Box>
      )}

      {activeTab === "baseline" && (
        <Box sx={{ width: "100%" }}>
          <BaselineEmission
            isPortfolioView={true}
            isClientPortfolio={isClientView}
            portfolioFilter={portfolioFilter}
            selectedScopeFilter={toScopeIds(selectedScopeFilter)}
            searchClickVersion={searchClickVersion}
            selectedOrganizations={getBaselineOrganizations(
              isClientView,
              orgId,
              organizationName,
              organizationsList,
              selectedOrganizations
            )}
          />
        </Box>
      )}
      <Box sx={{ display: isProjectTab ? "block" : "none" }}>
        <ProjectRegisterSection
          activeTab={activeTab}
          locationIds={selectedLocationIds}
          scopeFilter={selectedScopeFilter}
          isClientView={IsClientView}
          searchVersion={projectSearchVersion}
          portfolioGlidepathData={portfolioGlidepathData}
          groupBy={projectChartGroupBy}
          onGroupByChange={setProjectChartGroupBy}
          locationScenarios={Object.entries(normalizedScenarioSections)
            .filter(([id]) => selectedLocationIds.includes(Number(id)))
            .map(([id, section]) => ({
              locationId: Number(id),
              scenarioSection: section,
            }))}
        />
      </Box>

      <DecarbPlansDrawer
        open={decarbPlansOpen}
        onClose={() => setDecarbPlansOpen(false)}
        onApply={handleApplyDecarbPlans}
        locationSections={filteredLocationSections}
        locationsList={locationsList}
        appliedSelections={normalizedScenarioSections}
      />
    </Box>
  );
};

export default PortfolioView;
